/**
 * Auth context — Zitadel OIDC.
 *
 * Historically this was a dual MSAL provider (B2C for participants,
 * Entra ID for admins). It is now a single Zitadel client. The hook
 * export name `useDualMsal` is preserved during transition so existing
 * callers continue to work; once all consumers are migrated rename to
 * `useAuth`.
 *
 * Admin federation through Entra is a Zitadel-side concern — to this
 * code, every user is simply a Zitadel user.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { UserManager } from "oidc-client-ts";
import {
  zitadelConfig,
  loginRequests,
  buildIdpScopeRequest,
} from "../conf/authConfig";
import sharedConfig from "../../shared-config.json";

// IdP IDs for the LoginDialog (return-user sign-in). The /invite path
// gates these behind a backend invitation lookup, but for return
// logins the IdP IDs are bundled here. They're not secrets — they
// surface in the OIDC redirect URL on every legitimate sign-in
// anyway, and auto-create=OFF on each IdP in Zitadel is the actual
// gate against unsolicited account creation.
const RETURN_LOGIN_IDP_IDS = {
  entra: sharedConfig.auth?.entraIdpId,
  google: sharedConfig.auth?.googleIdpId,
  microsoft: sharedConfig.auth?.microsoftIdpId,
};

const AuthContext = createContext(null);

export const useDualMsal = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useDualMsal must be used within an AuthProvider");
  }
  return ctx;
};

const ROLES_CLAIM = sharedConfig.auth?.rolesClaim || "urn:zitadel:iam:org:project:roles";

// Consecutive `silentRenewError` events tolerated before we force a
// full interactive login. Each event fires once per renewal cycle
// (~60s before access token expiry), so 3 ≈ 3 cycles of failure before
// we intervene. Lower = quicker recovery but more disruption on
// transient network blips; higher = quieter logs but longer zombie
// window if cross-site cookie blocking is in effect (see below).
const SILENT_RENEW_FAILURE_LIMIT = 3;

const extractRoles = (profile) => {
  const claim = profile?.[ROLES_CLAIM];
  if (!claim || typeof claim !== "object") return [];
  return Object.keys(claim);
};

const buildCurrentUser = (user) => {
  if (!user || !user.profile) return null;
  const p = user.profile;
  const roles = extractRoles(p);
  return {
    userId: p.sub,
    name: p.name || p.preferred_username || p.email,
    email: p.email,
    emailVerified: p.email_verified,
    roles,
    // Legacy shape compatibility: pre-Zitadel code used authSource to
    // distinguish participants (b2c) from admins (entra). We now infer
    // from roles instead; keep the property for any remaining readers.
    authSource: roles.includes("PRADAM.Admin") || roles.includes("PRADAM.PI")
      ? "entra"
      : "b2c",
    // amr lets gated code check whether the user federated through
    // Entra (["external_idp"]) vs local password (["pwd"]).
    amr: p.amr || [],
  };
};

export function MsalProvider({ children }) {
  const userManagerRef = useRef(null);
  if (!userManagerRef.current) {
    userManagerRef.current = new UserManager(zitadelConfig);
  }
  const um = userManagerRef.current;
  const silentRenewFailuresRef = useRef(0);

  const [currentUser, setCurrentUser] = useState(null);
  const [authData, setAuthData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const applyUser = useCallback((user) => {
    if (!user || user.expired) {
      setCurrentUser(null);
      setAuthData(null);
      setIsAuthenticated(false);
      return;
    }
    setCurrentUser(buildCurrentUser(user));
    setAuthData({
      accessToken: user.access_token,
      idToken: user.id_token,
      expiresAt: user.expires_at,
    });
    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        // Handle the OIDC callback if this page load is a redirect back
        // from Zitadel (e.g. /auth/callback?code=...).
        const href = window.location.href;
        if (href.includes("code=") && href.includes("state=")) {
          try {
            let user = await um.signinRedirectCallback();
            // Deferred-creation invite flow: if `inviteState` was
            // stashed in the user-state arg to signinRedirect (see
            // src/pages/Invite.js), we're returning from federation
            // as a freshly-redeemed invitee. Complete the redemption
            // (role grant + Cosmos user / Participants upsert) using
            // the JWT we just got back, then silent-renew to pick up
            // the newly-granted role claim before applyUser routes
            // the user. Without the renew, currentUser.roles is empty
            // and SecureRoute bounces the participant to /.
            const inviteState = user?.state?.inviteState;
            if (inviteState && user.access_token) {
              try {
                const res = await fetch(
                  "/api/complete-invite-redemption",
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${user.access_token}`,
                    },
                    body: JSON.stringify({ inviteState }),
                  }
                );
                if (!res.ok) {
                  const errBody = await res.json().catch(() => ({}));
                  console.error(
                    "complete-invite-redemption failed:",
                    res.status,
                    errBody?.message
                  );
                  // Fall through to applyUser anyway. Without the role
                  // grant they'll hit a no-access experience — better
                  // than a hard error mid-callback. The orphan Zitadel
                  // account is harmless (no role, no Cosmos doc).
                } else {
                  try {
                    const renewed = await um.signinSilent();
                    if (renewed) user = renewed;
                  } catch (renewErr) {
                    console.warn(
                      "Silent renew after invite completion failed; user may need to refresh:",
                      renewErr?.message
                    );
                  }
                }
              } catch (compErr) {
                console.error(
                  "complete-invite-redemption threw:",
                  compErr?.message
                );
              }
            }
            if (!cancelled) applyUser(user);
            // Strip the code/state params from the URL
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            );
          } catch (cbErr) {
            console.warn("OIDC callback failed:", cbErr?.message);
          }
        } else {
          const user = await um.getUser();
          if (!cancelled) applyUser(user);
        }
      } finally {
        if (!cancelled) setIsInitialized(true);
      }
    };

    init();

    const onLoaded = (user) => {
      // A successful (silent or interactive) renewal clears the
      // failure counter so transient blips don't compound across
      // long-lived sessions.
      silentRenewFailuresRef.current = 0;
      applyUser(user);
    };
    const onUnloaded = () => applyUser(null);
    const onAccessTokenExpired = () => applyUser(null);

    // Why this exists:
    //
    // oidc-client-ts attempts silent renewal via two paths:
    //   1. POST /oauth/v2/token with grant_type=refresh_token — works
    //      whenever a refresh_token was issued at login.
    //   2. Hidden iframe → /oauth/v2/authorize?prompt=none — the
    //      fallback when no refresh_token is in storage.
    //
    // Path 2 depends on the Zitadel session cookie reaching auth.delosis.com
    // through an iframe whose parent is a *different site* (e.g.
    // main.pradam.pages.dev or pradam.org). Modern browsers strip
    // third-party cookies in cross-site iframes, so Zitadel sees no
    // session, redirects the iframe to /ui/v2/login/login, and the
    // SPA never gets a code back. oidc-client-ts retries every cycle,
    // forever — generating a stream of `invalid token (APP-Reb32)`
    // warnings in Zitadel and never recovering.
    //
    // Once we exceed SILENT_RENEW_FAILURE_LIMIT consecutive failures
    // we assume the iframe path is structurally broken (not a transient
    // network issue) and bounce the user through an interactive login.
    // That returns to /oauth/v2/token via the auth code flow, which
    // now (post-2026-05-24) issues a refresh_token and gets us off the
    // iframe path entirely until the refresh_token itself expires.
    const onSilentRenewError = (err) => {
      silentRenewFailuresRef.current += 1;
      const count = silentRenewFailuresRef.current;
      console.warn(
        `OIDC silent renew failed (${count}/${SILENT_RENEW_FAILURE_LIMIT}):`,
        err?.message || err,
      );
      if (count >= SILENT_RENEW_FAILURE_LIMIT) {
        silentRenewFailuresRef.current = 0;
        um.removeUser()
          .catch(() => {})
          .finally(() => {
            um.signinRedirect().catch((redirErr) => {
              console.error("Forced re-login after silent renew failures failed:", redirErr);
            });
          });
      }
    };

    um.events.addUserLoaded(onLoaded);
    um.events.addUserUnloaded(onUnloaded);
    um.events.addAccessTokenExpired(onAccessTokenExpired);
    um.events.addSilentRenewError(onSilentRenewError);

    return () => {
      cancelled = true;
      um.events.removeUserLoaded(onLoaded);
      um.events.removeUserUnloaded(onUnloaded);
      um.events.removeAccessTokenExpired(onAccessTokenExpired);
      um.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [um, applyUser]);

  const getToken = useCallback(async () => {
    const user = await um.getUser();
    if (!user || user.expired) {
      // Try silent renew once before giving up.
      try {
        const renewed = await um.signinSilent();
        return renewed?.access_token || null;
      } catch {
        return null;
      }
    }
    return user.access_token;
  }, [um]);

  const logout = useCallback(async () => {
    try {
      await um.signoutRedirect();
    } catch (e) {
      // Fall back to local clear if Zitadel end-session fails.
      await um.removeUser();
      applyUser(null);
    }
  }, [um, applyUser]);

  // Login helpers. Each returns a Promise that redirects the browser
  // to Zitadel — callers should not expect it to resolve before unload.
  const redirectWithRequest = useCallback(
    (request) => {
      if (!request) {
        console.warn(
          "Login method not configured — check idpIds in authConfig.js"
        );
        return Promise.resolve();
      }
      // LoginDialog stashes the typed email under sessionStorage.login_hint.
      // oidc-client-ts does NOT auto-forward it (despite an old comment that
      // claimed otherwise), so we explicitly inject it into the auth request.
      // The hint is consumed once and cleared so it doesn't bleed into a
      // later sign-in by a different person on the same device.
      let hint = null;
      try {
        hint = window.sessionStorage.getItem("login_hint");
        if (hint) window.sessionStorage.removeItem("login_hint");
      } catch {
        // ignore
      }
      const req = hint ? { ...request, login_hint: hint } : request;
      return um.signinRedirect(req);
    },
    [um]
  );

  // Legacy-shaped b2c/entra objects so existing LoginDialog callers
  // keep working. The IdP-specific methods build an IDP_SCOPE
  // signinRedirect using the IdP ID from shared-config — that's the
  // Zitadel V2 mechanism for skipping the chooser and going straight
  // to the chosen IdP. Without this, every "Sign in with X" button
  // landed on Zitadel's auth-method chooser asking for password vs
  // a list of IdPs, which is exactly what we DON'T want.
  const b2c = {
    signIn: () => redirectWithRequest(loginRequests.email()),
    signUp: () => redirectWithRequest(loginRequests.signup()),
    signInEmail: () => redirectWithRequest(loginRequests.email()),
    signInMicrosoft: () =>
      redirectWithRequest(buildIdpScopeRequest(RETURN_LOGIN_IDP_IDS.microsoft)),
    signInGoogle: () =>
      redirectWithRequest(buildIdpScopeRequest(RETURN_LOGIN_IDP_IDS.google)),
    signUpEmail: () => redirectWithRequest(loginRequests.signup()),
    signUpMicrosoft: () =>
      redirectWithRequest(buildIdpScopeRequest(RETURN_LOGIN_IDP_IDS.microsoft)),
    signUpGoogle: () =>
      redirectWithRequest(buildIdpScopeRequest(RETURN_LOGIN_IDP_IDS.google)),
    passwordReset: () => redirectWithRequest(loginRequests.passwordReset()),
  };

  const entra = {
    signIn: () =>
      redirectWithRequest(buildIdpScopeRequest(RETURN_LOGIN_IDP_IDS.entra)),
  };

  const value = {
    // New-style
    userManager: um,
    currentUser,
    authData,
    isAuthenticated,
    isInitialized,
    getToken,
    logout,
    // Legacy-shape passthroughs (keep existing consumers working)
    b2c,
    entra,
    // Helpers for code still probing specific shapes
    getCurrentUser: () => currentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
